import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, GlobalStyles, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
    Toolbar, Separator, HtmlButton,
    BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough,
    BtnNumberedList, BtnBulletList, BtnLink, BtnClearFormatting,
    BtnUndo, BtnRedo, createButton, useEditorState,
} from 'react-simple-wysiwyg';
import TitleIcon from '@mui/icons-material/Title';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// The full text-editor toolbar for the webshop's content texts (pages, footer
// columns, cart cards, look intro, text containers). It brings back what the
// old admin's editor had — headings, font size, text and background colour,
// alignment, indent, image and video — while staying on one line: the less
// used groups sit behind small icon menus. Use it inside an
// <EditorProvider><Editor> like the plain <Toolbar>.

const iconSx = { fontSize: 18, verticalAlign: 'middle' };

// One line where it fits; a very narrow editor wraps instead of overflowing.
const toolbarStyles = {
    '.sft-rte-toolbar': { flexWrap: 'wrap' },
    '.sft-rte-toolbar .sft-rte-menu': { display: 'inline-flex', alignItems: 'center', width: 'auto', padding: '0 2px' },
};

// Only web addresses: a pasted `javascript:` url must never end up in the page.
const webUrl = (raw) => {
    const url = String(raw || '').trim();
    return /^https?:\/\//i.test(url) ? url : null;
};

// YouTube / Vimeo page links → their embeddable player url.
export const videoEmbedUrl = (raw) => {
    const url = webUrl(raw);
    if (!url) return null;
    let m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
    return url;
};

const escAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

// A toolbar button that opens a small menu. The text selection is remembered
// when the menu opens and put back before the command runs — clicking in the
// menu would otherwise drop it and the command would hit nothing.
const MenuButton = ({ title, icon, items }) => {
    const editorState = useEditorState();
    const [anchor, setAnchor] = useState(null);
    const rangeRef = useRef(null);
    if (editorState.htmlMode) return null;
    const el = editorState.$el;
    const open = (e) => {
        e.preventDefault();
        const sel = window.getSelection();
        rangeRef.current = (sel && sel.rangeCount && el && el.contains(sel.anchorNode)) ? sel.getRangeAt(0).cloneRange() : null;
        setAnchor(e.currentTarget);
    };
    const pick = (item) => {
        setAnchor(null);
        if (el) el.focus();
        const range = rangeRef.current;
        if (range) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
        document.execCommand(item.command, false, item.arg);
    };
    return (
        <>
            <button type="button" className="rsw-btn sft-rte-menu" title={title} tabIndex={-1} onMouseDown={open} data-rte-menu={title}>
                {icon}<ArrowDropDownIcon sx={{ fontSize: 14, ml: -0.25 }} />
            </button>
            <Menu
                anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
                disableAutoFocus disableAutoFocusItem disableRestoreFocus
                slotProps={{ paper: { sx: { minWidth: 160 } } }}
            >
                {items.map((item) => (
                    <MenuItem key={`${item.command}:${item.arg}`} dense onMouseDown={(e) => e.preventDefault()} onClick={() => pick(item)}>
                        {(item.icon || item.swatch) && (
                            <ListItemIcon sx={{ minWidth: 30 }}>
                                {item.icon || (
                                    <Box sx={{
                                        width: 16, height: 16, borderRadius: 0.5,
                                        border: '1px solid rgba(0,0,0,0.2)',
                                        background: item.swatch === 'transparent'
                                            ? 'repeating-conic-gradient(#e7e7e7 0deg 90deg, #fff 90deg 180deg) 0 0 / 8px 8px'
                                            : item.swatch,
                                    }} />
                                )}
                            </ListItemIcon>
                        )}
                        <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13, ...(item.style || {}) }} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
MenuButton.propTypes = { title: PropTypes.string, icon: PropTypes.node, items: PropTypes.array };

const RichTextToolbar = ({ t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
    const lang = typeof t === 'function' ? t('cfg_rte_style', 'Style') : '';

    // Menus and button factories, built once per language (a new component
    // type every render would remount the toolbar).
    const M = useMemo(() => {
        const colors = [
            [tr('cfg_rte_color_black', 'Black'), '#000000'],
            [tr('cfg_rte_color_grey', 'Grey'), '#666666'],
            [tr('cfg_rte_color_red', 'Red'), '#c62828'],
            [tr('cfg_rte_color_orange', 'Orange'), '#ef6c00'],
            [tr('cfg_rte_color_green', 'Green'), '#2e7d32'],
            [tr('cfg_rte_color_blue', 'Blue'), '#1565c0'],
            [tr('cfg_rte_color_white', 'White'), '#ffffff'],
        ];
        return {
            styles: [
                { label: tr('cfg_rte_style_normal', 'Normal'), command: 'formatBlock', arg: 'P' },
                { label: tr('cfg_rte_style_h1', 'Heading 1'), command: 'formatBlock', arg: 'H1', style: { fontSize: 18, fontWeight: 700 } },
                { label: tr('cfg_rte_style_h2', 'Heading 2'), command: 'formatBlock', arg: 'H2', style: { fontSize: 16, fontWeight: 700 } },
                { label: tr('cfg_rte_style_h3', 'Heading 3'), command: 'formatBlock', arg: 'H3', style: { fontSize: 14, fontWeight: 700 } },
                { label: tr('cfg_rte_style_quote', 'Quote'), command: 'formatBlock', arg: 'BLOCKQUOTE', style: { fontStyle: 'italic' } },
            ],
            sizes: [
                { label: tr('cfg_rte_size_small', 'Small'), command: 'fontSize', arg: '2', style: { fontSize: 11 } },
                { label: tr('cfg_rte_size_normal', 'Normal'), command: 'fontSize', arg: '3', style: { fontSize: 13 } },
                { label: tr('cfg_rte_size_large', 'Large'), command: 'fontSize', arg: '5', style: { fontSize: 17 } },
                { label: tr('cfg_rte_size_xlarge', 'Very large'), command: 'fontSize', arg: '6', style: { fontSize: 21 } },
            ],
            color: colors.map(([label, hex]) => ({ label, command: 'foreColor', arg: hex, swatch: hex })),
            background: [
                { label: tr('cfg_rte_color_none', 'None'), command: 'hiliteColor', arg: 'transparent', swatch: 'transparent' },
                ...colors.map(([label, hex]) => ({ label, command: 'hiliteColor', arg: hex, swatch: hex })),
            ],
            align: [
                { label: tr('cfg_rte_align_left', 'Align left'), command: 'justifyLeft', arg: '', icon: <FormatAlignLeftIcon sx={iconSx} /> },
                { label: tr('cfg_rte_align_center', 'Center'), command: 'justifyCenter', arg: '', icon: <FormatAlignCenterIcon sx={iconSx} /> },
                { label: tr('cfg_rte_align_right', 'Align right'), command: 'justifyRight', arg: '', icon: <FormatAlignRightIcon sx={iconSx} /> },
                { label: tr('cfg_rte_align_justify', 'Justify'), command: 'justifyFull', arg: '', icon: <FormatAlignJustifyIcon sx={iconSx} /> },
                { label: tr('cfg_rte_indent', 'Indent'), command: 'indent', arg: '', icon: <FormatIndentIncreaseIcon sx={iconSx} /> },
                { label: tr('cfg_rte_outdent', 'Outdent'), command: 'outdent', arg: '', icon: <FormatIndentDecreaseIcon sx={iconSx} /> },
            ],
            Image: createButton(tr('cfg_rte_image', 'Image'), <ImageOutlinedIcon sx={iconSx} />, () => {
                // eslint-disable-next-line no-alert
                const url = webUrl(window.prompt(tr('cfg_rte_image_prompt', 'Web address of the image (https://…)')));
                if (url) document.execCommand('insertImage', false, url);
            }),
            Video: createButton(tr('cfg_rte_video', 'Video'), <OndemandVideoOutlinedIcon sx={iconSx} />, () => {
                // eslint-disable-next-line no-alert
                const src = videoEmbedUrl(window.prompt(tr('cfg_rte_video_prompt', 'Link to the video (YouTube, Vimeo or https://…)')));
                if (src) {
                    document.execCommand('insertHTML', false,
                        `<iframe src="${escAttr(src)}" width="560" height="315" style="max-width:100%" frameborder="0" allowfullscreen></iframe>`);
                }
            }),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    return (
        <>
            <GlobalStyles styles={toolbarStyles} />
            <Toolbar className="rsw-toolbar sft-rte-toolbar">
                <BtnUndo />
                <BtnRedo />
                <Separator />
                <MenuButton title={tr('cfg_rte_style', 'Style')} icon={<TitleIcon sx={iconSx} />} items={M.styles} />
                <MenuButton title={tr('cfg_rte_size', 'Font size')} icon={<FormatSizeIcon sx={iconSx} />} items={M.sizes} />
                <Separator />
                <BtnBold />
                <BtnItalic />
                <BtnUnderline />
                <BtnStrikeThrough />
                <MenuButton title={tr('cfg_rte_color', 'Text colour')} icon={<FormatColorTextIcon sx={iconSx} />} items={M.color} />
                <MenuButton title={tr('cfg_rte_background', 'Background colour')} icon={<FormatColorFillIcon sx={iconSx} />} items={M.background} />
                <Separator />
                <MenuButton title={tr('cfg_rte_align', 'Alignment and indent')} icon={<FormatAlignLeftIcon sx={iconSx} />} items={M.align} />
                <BtnNumberedList />
                <BtnBulletList />
                <Separator />
                <BtnLink />
                <M.Image />
                <M.Video />
                <Separator />
                <BtnClearFormatting />
                <HtmlButton title={tr('cfg_rte_source', 'Show the HTML source')} />
            </Toolbar>
        </>
    );
};

RichTextToolbar.propTypes = { t: PropTypes.func };

export default RichTextToolbar;
